import { api } from "@/lib/api/client";

import type {
  MovementType,
  Product,
  ProductCategory,
  ProductCategoryListResponse,
  ProductListResponse,
  RegularisePayload,
  RegulariseResult,
  StockMovementListResponse,
  StockTransferPayload,
  StockTransferResult,
  TransferTarget,
} from "./types";

interface ListParams {
  search?: string;
  category?: string;
  is_active?: boolean;
  low_stock?: boolean;
  /** true = actes seulement ; false = articles seulement. */
  is_service?: boolean;
  page?: number;
  page_size?: number;
  ordering?: string;
  /* Variantes (ADR 0055), lus seulement quand le module est actif. */
  variant_of?: string;
  is_template?: boolean;
  /** false = modèles et produits simples (les variantes se rangent sous leur modèle). */
  is_variant?: boolean;
  /** Ids de valeurs séparés par des virgules : variantes qui les portent toutes. */
  attribute_values?: string;
  /* Catalogue avancé (ADR 0058), lus seulement quand le module est actif. */
  brand?: string;
  price_min?: string;
  price_max?: string;
  /** Rupture, stock bas ou disponible (stock de l'agence avec le stock par agence). */
  stock_state?: "out" | "low" | "available";
  /** Articles en stock sans aucun coût (module « Contrôle du stock », ADR 0070). */
  missing_cost?: boolean;
}

interface CreatePayload {
  sku: string;
  name: string;
  brand?: string;
  /** Facultative pour un acte ; l'écran des produits l'exige. */
  category?: string | null;
  sale_price?: string;
  cost_price?: string | null;
  tax_rate?: string;
  barcode?: string;
  description?: string;
  low_stock_threshold?: number;
  is_active?: boolean;
  /** Fixé à la création (ADR 0047). */
  is_service?: boolean;
  /** Modèle à variantes, fixé à la création (ADR 0055). */
  is_template?: boolean;
  /** Variante : reprendre le prix du modèle (ADR 0055). */
  price_inherited?: boolean;
  /** Unité de l'article (espace Articles, ADR 0062), module actif seulement. */
  unit?: string | null;
  /** Article suivi par lot (ADR 0062), module actif seulement. */
  track_lots?: boolean;
}

type UpdatePayload = Partial<CreatePayload>;

export const inventoryApi = {
  list: async (params: ListParams = {}): Promise<ProductListResponse> => {
    const { data } = await api.get<ProductListResponse>("/products/", { params });
    return data;
  },
  alerts: async (): Promise<Product[]> => {
    const { data } = await api.get<Product[]>("/products/alerts/");
    return data;
  },
  nextReference: async (isService = false): Promise<string> => {
    const { data } = await api.get<{ reference: string }>("/products/next-reference/", {
      params: isService ? { is_service: true } : undefined,
    });
    return data.reference;
  },
  create: async (payload: CreatePayload): Promise<Product> => {
    const { data } = await api.post<Product>("/products/", payload);
    return data;
  },
  get: async (id: string): Promise<Product> => {
    const { data } = await api.get<Product>(`/products/${id}/`);
    return data;
  },
  update: async (id: string, payload: UpdatePayload): Promise<Product> => {
    const { data } = await api.patch<Product>(`/products/${id}/`, payload);
    return data;
  },
  lastPrice: async (id: string): Promise<string | null> => {
    const { data } = await api.get<{ unit_price: string | null }>(`/products/${id}/last-price/`);
    return data.unit_price;
  },
  movements: async (
    params: {
      page?: number;
      page_size?: number;
      ordering?: string;
      product?: string;
      search?: string;
      date_from?: string;
      date_to?: string;
      /** Ventes prises sur le stock commun, à régulariser (stock par agence). */
      to_regularise?: boolean;
      /** Avec `to_regularise` : montre aussi les mouvements déjà régularisés. */
      show_regularised?: boolean;
    } = {},
  ): Promise<StockMovementListResponse> => {
    const { data } = await api.get<StockMovementListResponse>("/stock-movements/", { params });
    return data;
  },
  categories: async (params: { search?: string } = {}): Promise<ProductCategory[]> => {
    const { data } = await api.get<ProductCategoryListResponse>("/product-categories/", {
      params: { page_size: 200, ...params },
    });
    return data.results;
  },
  category: async (id: string): Promise<ProductCategory> => {
    const { data } = await api.get<ProductCategory>(`/product-categories/${id}/`);
    return data;
  },
  createCategory: async (
    payload: { name: string; code?: string; is_active?: boolean },
  ): Promise<ProductCategory> => {
    const { data } = await api.post<ProductCategory>("/product-categories/", payload);
    return data;
  },
  updateCategory: async (
    id: string,
    payload: { name?: string; is_active?: boolean },
  ): Promise<ProductCategory> => {
    const { data } = await api.patch<ProductCategory>(`/product-categories/${id}/`, payload);
    return data;
  },
  categoryNextReference: async (): Promise<string> => {
    const { data } = await api.get<{ reference: string }>("/product-categories/next-reference/");
    return data.reference;
  },
  recordMovement: async (payload: {
    product: string;
    movement_type: MovementType;
    quantity: number;
    reason?: string;
    /* Lots (ADR 0062) : lus seulement module actif et article suivi par lot. */
    lot_number?: string;
    expiry_date?: string | null;
    lot?: string | null;
  }) => {
    const { data } = await api.post("/stock-movements/", payload);
    return data;
  },
  /** Destinations d'un transfert (stock par agence) ; le stock commun n'est
   *  proposé que si l'utilisateur le gère pour ce produit. */
  transferTargets: async (product?: string): Promise<TransferTarget[]> => {
    const { data } = await api.get<TransferTarget[]>("/stock-movements/transfer-targets/", {
      params: product ? { product } : undefined,
    });
    return data;
  },
  /** Marque comme régularisés des mouvements de la liste « À régulariser ». */
  regulariseMovements: async (payload: RegularisePayload): Promise<RegulariseResult> => {
    const { data } = await api.post<RegulariseResult>("/stock-movements/regularise/", payload);
    return data;
  },
  transferStock: async (payload: StockTransferPayload): Promise<StockTransferResult> => {
    const { data } = await api.post<StockTransferResult>("/stock-movements/transfer/", payload);
    return data;
  },
};
